
import Vue from 'vue';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import {
  FileDescriptor, FileDocument, IngestDescriptor, IngestDocument, IngestFragment, UUID,
  Label,
  Suggestion,
  IngestFragmentBox,
  isIngestTextFragment,
  IngestTextFragment,
  Language,
  ExtractedEntity,
  EntityType,
} from '@/model';
import { notEmpty } from '@/util/array-util';
import api from '@/rest-api';
import SkeletonLoader from '@/components/SkeletonLoader.vue';
import IngestDocumentViewer from '@/components/IngestDocumentViewer.vue';
import IngestionPicker from '@/components/IngestionPicker.vue';
import DocumentFragmentScroller from '@/components/DocumentFragmentScroller.vue';
import IngestStats from '@/components/IngestStats.vue';
import SuggestionStepper from '@/components/SuggestionStepper.vue';
import DocumentEditor from '@/components/DocumentEditor.vue';
import DocumentHeader from '@/components/DocumentHeader.vue';
import labels from '@/store/labels';
import IconButton from '@/components/IconButton.vue';
import { documentResultPinnedTypes as configuredDocumentResultPinnedTypes, summariesEnabled } from '@/store/ui-config';

export default Vue.extend({
  name: 'DocumentResultView',
  components: {
    SkeletonLoader,
    IngestDocumentViewer,
    IngestionPicker,
    Splitpanes,
    Pane,
    DocumentFragmentScroller,
    IngestStats,
    SuggestionStepper,
    DocumentEditor,
    DocumentHeader,
    IconButton,
  },

  props: {
    loading: { type: Boolean, default: false },
    document: { type: Object as () => FileDocument | undefined, required: false },
    ingest: { type: Object as () => IngestDocument | undefined, required: false },
    suggestions: { type: Array as () => Suggestion [] | undefined, required: false },
    descriptor: { type: Object as () => IngestDescriptor | undefined, required: false },
    descriptors: { type: Array as () => IngestDescriptor[], default: () => [] },
    media: { type: Array as () => FileDescriptor[], default: () => [] },
    fragments: { type: Array as () => IngestFragment[], default: () => [] },
    scrollToFragmentId: { type: Number as () => number | undefined, required: false },
    extractedEntities: { type: Array as () => ExtractedEntity[], default: () => [] },
    entityTypes: { type: Array as () => EntityType[], default: () => [] },
  },

  data() {
    return {
      showIngest: false,
      renderedFragments: [] as Array<{ fragment: IngestFragment; top: number; actual: boolean; hover: (value: boolean) => void }>,
      translate: false,
      styleDialog: false,
      hierarchyDialog: false,
      targetLanguage: 'en',
      editDialog: false,
      editFragment: undefined as IngestFragment | undefined,
      zoom: 0.90,
      zoomLimit: { upper: 1.5, lower: 0.5, start: 0.90 },
      stats: false,
      entitiesDialog: false,
      suggestion: undefined as Suggestion | undefined,
      showSuggestions: false,
      indexMenu: false,
      settings: {
        textStyle: true,
        colors: true,
        fontScale: 90.0,
        wrapText: false,
        hierarchyAutoScroll: true,
        hideImageHierarchyContainers: false,
      },
      translateOptions: {
        additionalInstructions: undefined as string | undefined,
        includeContext: undefined as boolean | undefined,
      },
      selectedText: undefined as string | undefined,
      activeHierarchy: [] as number[],
      scrollToHierarchyId: null as number | null,
    };
  },

  mounted() {
    const settings = localStorage.getItem('documentResultView.settings');
    if (settings) {
      this.settings = { ...this.settings, ...JSON.parse(localStorage.getItem('documentResultView.settings') || '[]') };
    }
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  watch: {
    fragments(fragments) {
      this.renderedFragments = fragments
        .map((fragment) => {
          const rendered = this.renderedFragments.find((r) => r.fragment.id === fragment.id);
          return rendered && {
            fragment, top: rendered.top, actual: rendered.actual, hover: rendered.hover,
          };
        })
        .filter(notEmpty);
    },
    suggestions(suggestions) {
      if (this.suggestion && suggestions) {
        const suggestion = suggestions.find((s) => s.id === this.suggestion?.id);
        this.suggestionChange(suggestion, false);
      }
    },
    zoom() {
      this.renderedFragments = [];
    },
    document() {
      if (!this.document?.file) {
        this.showIngest = true;
      }
    },
    scrollToFragmentId: {
      immediate: true,
      handler(to) {
        if (to) {
          this.showIngest = true;
        }
      },
    },
    settings: {
      deep: true,
      handler() {
        localStorage.setItem('documentResultView.settings', JSON.stringify(this.settings));
      },
    },
    ingest: {
      immediate: true,
      handler(ingest) {
        if (ingest) {
          const pipeline = ingest.meta?.pipeline;
          if (pipeline === 'docling') {
            this.settings = {
              ...this.settings,
              textStyle: false,
              wrapText: true,
            };
          } else if (pipeline === 'textract') {
            this.settings = {
              ...this.settings,
              textStyle: true,
              wrapText: false,
            };
          } else {
            this.settings = { ...this.settings, wrapText: !!this.translated };
          }
        }
        this.activeHierarchy = [];
        this.scrollToHierarchyId = null;
      },
    },
    activeHierarchy(active: number[]) {
      const id = active.length > 0 ? active[0] : null;
      this.scrollToHierarchyId = this.settings.hierarchyAutoScroll ? id : null;
    },
  },

  computed: {
    summariesEnabled(): boolean {
      return summariesEnabled();
    },
    entityTypeNameMap(): Record<number, string> {
      const map: Record<number, string> = {};
      for (const et of this.entityTypes) map[et.id] = et.name;
      return map;
    },
    attributeNameMap(): Record<number, string> {
      const map: Record<number, string> = {};
      for (const et of this.entityTypes) {
        for (const attr of et.attributes) map[attr.id] = attr.attributeType;
      }
      return map;
    },
    highlights(): IngestFragmentBox[] | undefined {
      return this.showSuggestions ? [this.suggestion]
        .filter(notEmpty)
        .filter((s) => s.action && s.action.fragmentId === undefined) // only for rejected suggestions
        .map((s) => ({
          from: s.from, to: s.to, boxId: s.boxId, seqNo: undefined,
        })) : undefined;
    },
    documentResultPinnedTypes(): readonly string[] {
      return configuredDocumentResultPinnedTypes();
    },
    docLabels(): Label[] {
      return this.document?.labels
        .map((id) => labels.getters.lookup.get(id))
        .filter(notEmpty) || [];
    },
    translated(): Language | undefined {
      return this.ingest?.translated;
    },
    suggestionsAvailable(): boolean {
      return !!this.suggestions && this.suggestions.length > 0;
    },
    lastAnnotatedPageBoxId(): number | undefined {
      if (!this.ingest) {
        return undefined;
      }
      const fragmentBoxIds = new Set(
        this.fragments
          .filter(isIngestTextFragment)
          .flatMap((f) => f.boxes.map((b) => b.boxId)),
      );
      const pagesWithFragments = this.ingest.pages
        .filter((p) => p.boxes.some((b) => fragmentBoxIds.has(b.id)))
        .slice()
        .sort((a, b) => a.seqNo - b.seqNo);
      const lastPage = pagesWithFragments[pagesWithFragments.length - 1];
      return lastPage?.boxes.find((b) => fragmentBoxIds.has(b.id))?.id;
    },
    headings(): Array<{ id: number; text: string; page: number }> {
      if (!this.ingest) {
        return [];
      }
      const result: Array<{ id: number; text: string; page: number }> = [];
      this.ingest.pages.forEach((page, index) => {
        page.boxes.forEach((box) => {
          const kind = (box.type || '').toLowerCase();
          if (!kind.includes('heading')) {
            return;
          }
          const text = (box.text || '').trim();
          if (!text) {
            return;
          }
          result.push({ id: box.id, text, page: page.seqNo || index + 1 });
        });
      });
      return result;
    },
    hierarchyTree(): Array<{ id: number; label: string; children?: any[] }> {
      if (!this.ingest?.hierarchies || this.ingest.hierarchies.length === 0) {
        return [];
      }
      const byId = new Map(this.ingest.hierarchies.map((h) => [h.id, h]));
      const childrenByParent = new Map<number, number[]>();
      this.ingest.hierarchies.forEach((h) => {
        if (h.parentId != null) {
          const list = childrenByParent.get(h.parentId) || [];
          list.push(h.id);
          childrenByParent.set(h.parentId, list);
        }
      });
      const toNode = (id: number): any => {
        const h = byId.get(id);
        if (!h) {
          return undefined;
        }
        const count = h.boxIds?.length || 0;
        const meta = [
          h.type ? `${h.type}` : undefined,
          h.source ? `${h.source}` : undefined,
          count > 0 ? `${count} boxes` : undefined,
        ].filter(notEmpty).join(' • ');
        const label = [h.ref, meta].filter(notEmpty).join(' — ');
        const children = (childrenByParent.get(id) || [])
          .map((childId) => toNode(childId))
          .filter(notEmpty);
        return { id: h.id, label, children: children.length > 0 ? children : undefined };
      };
      return this.ingest.hierarchies
        .filter((h) => h.parentId == null)
        .map((h) => toNode(h.id))
        .filter(notEmpty);
    },
    activeHierarchyId(): number | null {
      return this.activeHierarchy.length > 0 ? this.activeHierarchy[0] : null;
    },
  },

  methods: {
    entityBoxId(entity: ExtractedEntity): number | undefined {
      return entity.spans[0]?.boxId;
    },
    fileSource(uuid: UUID): string {
      return api.fileDownloadUri(uuid);
    },
    isInlineFile(file: FileDescriptor) {
      return file.mime === 'application/pdf';
    },
    changeIngestion(descriptor: IngestDescriptor) {
      this.$emit('changeIngestion', descriptor);
    },
    onFragmentsRendered(rendered: Array<{ id: number; top: number; first: boolean; actual: boolean; hover: (value: boolean) => void }>) {
      rendered.forEach((r) => {
        const fragment = this.fragments.find((f) => f.id === r.id);
        if (fragment) {
          const renderedFragment = {
            fragment, top: r.top, actual: r.actual, hover: r.hover,
          };
          const index = this.renderedFragments.findIndex((f) => f.fragment.id === fragment.id);
          if (index > -1) {
            const existing = this.renderedFragments[index];
            let top: number;
            // Should not update top if editing a fragment,
            if (this.editFragment !== undefined && r.id !== 0) {
              top = existing.top;
            } else if (!existing.actual && r.actual) {
              top = r.top;
            } else {
              top = Math.min(existing.top, r.top);
            }
            Vue.set(
              this.renderedFragments,
              index,
              {
                ...renderedFragment,
                top,
                hover: (value: boolean) => { renderedFragment.hover(value); existing.hover(value); },
              },
            );
          } else {
            this.renderedFragments.push(renderedFragment);
          }
          if (fragment.id === this.scrollToFragmentId && r.first && isIngestTextFragment(fragment)) {
            const box = fragment.boxes[0];
            if (box) {
              this.scrollTo(box.boxId);
            }
          }
        }
      });
    },
    onSaveFragment(fragment: IngestFragment) {
      this.$emit('saveFragment', fragment, this.suggestion);
      this.editFragment = undefined;
      this.selectedText = undefined;
    },
    onDeleteFragment(id: number) {
      this.$emit('deleteFragment', id);
      this.selectedText = undefined;
    },
    onRevertFragment(id: number) {
      this.$emit('revertFragment', id);
      this.selectedText = undefined;
    },
    onDocumentSaved() {
      this.$emit('load', true);
      this.editDialog = false;
    },

    createTranslateJob() {
      this.$emit('createJob', {
        type: 'translate',
        documents: [this.document],
        options: {
          ingestId: this.ingest?.id,
          additionalInstructions: this.translateOptions.additionalInstructions,
          includeContext: this.translateOptions.includeContext,
        },
      });
      this.translate = false;
    },
    edit(fragment: IngestFragment | undefined) {
      this.editFragment = fragment;
      if (fragment && isIngestTextFragment(fragment)) {
        this.selectedText = this.fragmentText(fragment);
      }
    },
    onSelection(fragment: IngestFragment, clear?: boolean) {
      const existing = this.fragments.findIndex((f) => f.id === fragment.id);
      if (existing > -1) {
        Vue.set(this.fragments, existing, fragment);
        if (clear) {
          this.renderedFragments = this.renderedFragments.filter((f) => f.fragment.id !== fragment.id);
        }
      } else {
        Vue.set(this.fragments, this.fragments.length, fragment);
      }
      this.editFragment = fragment;
      if (isIngestTextFragment(fragment)) {
        this.selectedText = this.fragmentText(fragment);
      }
    },
    deselectFragment(fragment: IngestFragment | undefined) {
      if (fragment?.id === 0) {
        const index = this.fragments.findIndex((f) => f.id === fragment?.id);
        if (index > -1) {
          Vue.delete(this.fragments, index);
        }
      }
      this.editFragment = undefined;
      this.selectedText = undefined;
    },

    suggestionChange(suggestion: Suggestion | undefined, scrollTo = true) {
      this.suggestion = suggestion;
      if (suggestion) {
        if (scrollTo) {
          this.scrollTo(suggestion.boxId);
        }
        if (suggestion.action === undefined) {
          const fragment: IngestTextFragment = {
            labels: [],
            id: 0,
            type: 'text',
            boxes: [
              {
                from: suggestion.from,
                to: suggestion.to,
                boxId: suggestion.boxId,
                seqNo: undefined,
              },
            ],
            isolate: false,
          };
          this.onSelection(fragment, true);
        } else {
          this.deselectFragment(this.editFragment);
        }
      }
    },
    scrollTo(boxId: number) {
      const el = document.getElementById(`box-${boxId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    },
    saveSuggestionAction(suggestion: Suggestion, accept: boolean) {
      this.$emit('saveSuggestionAction', suggestion, accept);
    },
    removeAction(suggestion: Suggestion) {
      this.$emit('removeAction', suggestion);
    },
    handleKeydown(event: KeyboardEvent) {
      if (event.ctrlKey && event.key === 'i') {
        this.showIngest = !this.showIngest;
        event.preventDefault();
      }
      if (event.ctrlKey && event.key === '+') {
        this.zoom += 0.1;
        event.preventDefault();
      }
      if (event.ctrlKey && event.key === '-') {
        this.zoom -= 0.1;
        event.preventDefault();
      }
      if (this.suggestionsAvailable && event.ctrlKey && event.key === 'b') {
        this.showSuggestions = !this.showSuggestions;
        event.preventDefault();
      }
      if (event.ctrlKey && event.key === 'End') {
        this.scrollToLastAnnotatedPage();
        event.preventDefault();
      }
    },
    scrollToLastAnnotatedPage() {
      if (this.lastAnnotatedPageBoxId) {
        this.scrollTo(this.lastAnnotatedPageBoxId);
      }
    },
    fragmentText(fragment: IngestTextFragment): string {
      const boxIds = fragment.boxes.map((b) => b.boxId);
      const documentBoxes = this.ingest?.pages.flatMap((p) => p.boxes).filter((b) => boxIds.includes(b.id)) || [];
      const text = fragment.boxes
        .map((b) => {
          const box = documentBoxes.find((d) => d.id === b.boxId);
          if (box) {
            return box.text?.substring(b.from, b.to) || '';
          }
          return '';
        })
        .join(' ');
      return text;
    },
    scrollToHeading(boxId: number) {
      this.scrollTo(boxId);
    },
  },

});
